import React, { useState, useEffect } from 'react';
import { Cliente, ClienteExoneracion } from '../../types';
import { API_CONFIG, buildApiUrl } from '../../config/api';

interface ClienteFormProps {
  cliente: Cliente;
  onSave: (cliente: Cliente) => void;
  onCancel: () => void;
}

// Configuración de tipos de documento para exoneraciones
const TIPOS_DOCUMENTO = {
  '01': 'Compras autorizadas por la Dirección General de Tributación',
  '02': 'Ventas exentas a diplomáticos',
  '03': 'Autorizado por ley especial',
  '04': 'Exenciones Dirección General de Hacienda Autorización Local Genérica',
  '05': 'Exenciones Dirección General de Hacienda Transitorio V (servicios de ingeniería, arquitectura, topografía obra civil)',
  '06': 'Servicios turísticos inscritos ante el Instituto Costarricense de Turismo (ICT)',
  '07': 'Transitorio XVII (Recolección, Clasificación, almacenamiento de Reciclaje y reutilizable)',
  '08': 'Exoneración a Zona Franca',
  '09': 'Exoneración de servicios complementarios para la exportación articulo 11 RLIVA',
  '10': 'Órgano de las corporaciones municipales',
  '11': 'Exenciones Dirección General de Hacienda Autorización de Impuesto Local Concreta',
  '99': 'Otros'
};

// Configuración de instituciones
const INSTITUCIONES = {
  '01': 'Ministerio de Hacienda',
  '02': 'Ministerio de Relaciones Exteriores y Culto',
  '03': 'Ministerio de Agricultura y Ganadería',
  '04': 'Ministerio de Economía, Industria y Comercio',
  '05': 'Cruz Roja Costarricense',
  '06': 'Benemérito Cuerpo de Bomberos de Costa Rica',
  '07': 'Asociación Obras del Espíritu Santo',
  '08': 'Federación Cruzada Nacional de protección al Anciano (Fecrunapa)',
  '09': 'Escuela de Agricultura de la Región Húmeda (EARTH)',
  '10': 'Instituto Centroamericano de Administración de Empresas (INCAE)',
  '11': 'Junta de Protección Social (JPS)',
  '12': 'Autoridad Reguladora de los Servicios Públicos (Aresep)',
  '99': 'Otros'
};

// Tipos que requieren artículo e inciso
const TIPOS_REQUIEREN_ARTICULO_INCISO = ['02', '03', '06', '07', '08'];

const ClienteForm: React.FC<ClienteFormProps> = ({ cliente, onSave, onCancel }) => {
  const [formData, setFormData] = useState<Cliente>({
    tipoidentificacion: '',
    numeroidentificacion: '',
    nombre: '',
    email: '',
    numerotelefono: '',
    direccion: '',
    activo: true
  });
  
  const [exoneraciones, setExoneraciones] = useState<ClienteExoneracion[]>([]);
  const [activeTab, setActiveTab] = useState<'datos' | 'exoneracion'>('datos');
  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState<Record<string, string>>({});

  useEffect(() => {
    if (cliente.id) {
      setFormData(cliente);
      fetchExoneraciones(cliente.id);
    }
  }, [cliente]);

  const fetchExoneraciones = async (clienteId: number) => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(
        `${buildApiUrl(API_CONFIG.CLIENTES.EXONERACIONES)}/${clienteId}/exoneraciones`,
        {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        }
      );

      if (response.ok) {
        const data = await response.json();
        setExoneraciones(data.exoneraciones || []);
      }
    } catch (error) {
      console.error('Error al cargar exoneraciones:', error);
    }
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => {
    const { name, value, type } = e.target;
    const checked = (e.target as HTMLInputElement).checked;
    
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));

    // Limpiar error del campo
    if (errors[name]) {
      setErrors(prev => ({ ...prev, [name]: '' }));
    }
  };

  const validateForm = (): boolean => {
    const newErrors: Record<string, string> = {};

    if (!formData.tipoidentificacion) {
      newErrors.tipoidentificacion = 'El tipo de identificación es requerido';
    }

    if (!formData.numeroidentificacion) {
      newErrors.numeroidentificacion = 'El número de identificación es requerido';
    }

    if (!formData.nombre) {
      newErrors.nombre = 'El nombre es requerido';
    }

    if (formData.email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      newErrors.email = 'El email no es válido';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const getTipoDocumentoLabel = (tipo: string): string => {
    return TIPOS_DOCUMENTO[tipo as keyof typeof TIPOS_DOCUMENTO] || tipo;
  };

  const getInstitucionLabel = (institucion: string): string => {
    return INSTITUCIONES[institucion as keyof typeof INSTITUCIONES] || institucion;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }

    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const url = formData.id 
        ? `${buildApiUrl(API_CONFIG.CLIENTES.UPDATE)}/${formData.id}`
        : buildApiUrl(API_CONFIG.CLIENTES.CREATE);
      
      const method = formData.id ? 'PUT' : 'POST';

      const response = await fetch(url, {
        method,
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(formData)
      });

      if (!response.ok) {
        throw new Error('Error al guardar el cliente');
      }

      const savedCliente = await response.json();
      
      // Si hay exoneraciones y es un cliente nuevo, guardarlas
      if (exoneraciones.length > 0 && !formData.id) {
        const clienteId = savedCliente.cliente.id;
        await saveExoneraciones(clienteId, exoneraciones);
      }
      
      // Si es un cliente existente y hay exoneraciones, actualizarlas
      if (exoneraciones.length > 0 && formData.id) {
        await updateExoneraciones(formData.id, exoneraciones);
      }

      onSave(savedCliente.cliente || savedCliente);
    } catch (error) {
      setErrors({ submit: error instanceof Error ? error.message : 'Error desconocido' });
    } finally {
      setLoading(false);
    }
  };

  const saveExoneraciones = async (clienteId: number, exoneracionesData: ClienteExoneracion[]) => {
    const token = localStorage.getItem('token');
    
    for (const exoneracion of exoneracionesData) {
      if (exoneracion.tipo_documento && exoneracion.numero_documento) {
        try {
          await fetch(buildApiUrl(API_CONFIG.CLIENTES.EXONERACIONES_CRUD), {
            method: 'POST',
            headers: {
              'Authorization': `Bearer ${token}`,
              'Content-Type': 'application/json'
            },
            body: JSON.stringify({
              ...exoneracion,
              cliente_id: clienteId
            })
          });
        } catch (error) {
          console.error('Error al guardar exoneración:', error);
        }
      }
    }
  };

  const updateExoneraciones = async (clienteId: number, exoneracionesData: ClienteExoneracion[]) => {
    const token = localStorage.getItem('token');
    
    // Primero obtener las exoneraciones existentes
    const response = await fetch(
      `${buildApiUrl(API_CONFIG.CLIENTES.EXONERACIONES)}/${clienteId}/exoneraciones`,
      {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      }
    );

    if (response.ok) {
      const existingExoneraciones = await response.json();
      
      // Eliminar exoneraciones existentes que no están en la nueva lista
      for (const existing of existingExoneraciones.exoneraciones || []) {
        const stillExists = exoneracionesData.some(ex => ex.id === existing.id);
        if (!stillExists) {
          await fetch(`${buildApiUrl(API_CONFIG.CLIENTES.EXONERACIONES_CRUD)}/${existing.id}`, {
            method: 'DELETE',
            headers: {
              'Authorization': `Bearer ${token}`,
              'Content-Type': 'application/json'
            }
          });
        }
      }
    }

    // Guardar/actualizar las nuevas exoneraciones
    for (const exoneracion of exoneracionesData) {
      if (exoneracion.tipo_documento && exoneracion.numero_documento) {
        try {
          if (exoneracion.id) {
            // Actualizar exoneración existente
            await fetch(`${buildApiUrl(API_CONFIG.CLIENTES.EXONERACIONES_CRUD)}/${exoneracion.id}`, {
              method: 'PUT',
              headers: {
                'Authorization': `Bearer ${token}`,
                'Content-Type': 'application/json'
              },
              body: JSON.stringify(exoneracion)
            });
          } else {
            // Crear nueva exoneración
            await fetch(buildApiUrl(API_CONFIG.CLIENTES.EXONERACIONES_CRUD), {
              method: 'POST',
              headers: {
                'Authorization': `Bearer ${token}`,
                'Content-Type': 'application/json'
              },
              body: JSON.stringify({
                ...exoneracion,
                cliente_id: clienteId
              })
            });
          }
        } catch (error) {
          console.error('Error al guardar/actualizar exoneración:', error);
        }
      }
    }
  };

  const addExoneracion = () => {
    const nuevaExoneracion: ClienteExoneracion = {
      cliente_id: formData.id || 0,
      tipo_documento: '',
      numero_documento: '',
      nombre_institucion: '',
      fecha_emision: new Date().toISOString().split('T')[0],
      tarifa: '',
      articulo: '',
      inciso: ''
    };
    setExoneraciones([...exoneraciones, nuevaExoneracion]);
  };

  const removeExoneracion = (index: number) => {
    setExoneraciones(exoneraciones.filter((_, i) => i !== index));
  };

  const updateExoneracion = (index: number, field: keyof ClienteExoneracion, value: string) => {
    const updated = [...exoneraciones];
    updated[index] = { ...updated[index], [field]: value };
    setExoneraciones(updated);
  };

  const requiresArticuloInciso = (tipoDocumento: string): boolean => {
    return TIPOS_REQUIEREN_ARTICULO_INCISO.includes(tipoDocumento);
  };

  return (
    <div className="bg-white shadow-md rounded-lg p-6 max-w-4xl mx-auto">
      <div className="flex justify-between items-center mb-6">
        <h2 className="text-2xl font-bold text-gray-800">
          {formData.id ? 'Editar Cliente' : 'Nuevo Cliente'}
        </h2>
        <button
          onClick={onCancel}
          className="text-gray-600 hover:text-gray-800"
        >
          ✕
        </button>
      </div>

      {/* Tabs */}
      <div className="border-b border-gray-200 mb-6">
        <nav className="-mb-px flex space-x-8">
          <button
            onClick={() => setActiveTab('datos')}
            className={`py-2 px-1 border-b-2 font-medium text-sm ${
              activeTab === 'datos'
                ? 'border-blue-500 text-blue-600'
                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
            }`}
          >
            Datos Básicos
          </button>
          <button
            onClick={() => setActiveTab('exoneracion')}
            className={`py-2 px-1 border-b-2 font-medium text-sm ${
              activeTab === 'exoneracion'
                ? 'border-blue-500 text-blue-600'
                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
            }`}
          >
            Exoneraciones
          </button>
        </nav>
      </div>

      <form onSubmit={handleSubmit}>
        {activeTab === 'datos' && (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {/* Tipo de Identificación */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Tipo de Identificación *
              </label>
              <select
                name="tipoidentificacion"
                value={formData.tipoidentificacion}
                onChange={handleInputChange}
                className={`w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                  errors.tipoidentificacion ? 'border-red-500' : 'border-gray-300'
                }`}
              >
                <option value="">Seleccionar tipo</option>
                <option value="01">Cédula</option>
                <option value="02">DIMEX</option>
                <option value="03">NITE</option>
              </select>
              {errors.tipoidentificacion && (
                <p className="text-red-500 text-sm mt-1">{errors.tipoidentificacion}</p>
              )}
            </div>

            {/* Número de Identificación */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Número de Identificación *
              </label>
              <input
                type="text"
                name="numeroidentificacion"
                value={formData.numeroidentificacion}
                onChange={handleInputChange}
                className={`w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                  errors.numeroidentificacion ? 'border-red-500' : 'border-gray-300'
                }`}
                placeholder="Ej: 123456789"
              />
              {errors.numeroidentificacion && (
                <p className="text-red-500 text-sm mt-1">{errors.numeroidentificacion}</p>
              )}
            </div>

            {/* Nombre */}
            <div className="md:col-span-2">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Nombre Completo *
              </label>
              <input
                type="text"
                name="nombre"
                value={formData.nombre}
                onChange={handleInputChange}
                className={`w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                  errors.nombre ? 'border-red-500' : 'border-gray-300'
                }`}
                placeholder="Nombre completo del cliente"
              />
              {errors.nombre && (
                <p className="text-red-500 text-sm mt-1">{errors.nombre}</p>
              )}
            </div>

            {/* Email */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Email
              </label>
              <input
                type="email"
                name="email"
                value={formData.email || ''}
                onChange={handleInputChange}
                className={`w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                  errors.email ? 'border-red-500' : 'border-gray-300'
                }`}
                placeholder="cliente@email.com"
              />
              {errors.email && (
                <p className="text-red-500 text-sm mt-1">{errors.email}</p>
              )}
            </div>

            {/* Teléfono */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Teléfono
              </label>
              <input
                type="tel"
                name="numerotelefono"
                value={formData.numerotelefono || ''}
                onChange={handleInputChange}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                placeholder="8888-8888"
              />
            </div>

            {/* Dirección */}
            <div className="md:col-span-2">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Dirección
              </label>
              <textarea
                name="direccion"
                value={formData.direccion || ''}
                onChange={handleInputChange}
                rows={3}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                placeholder="Dirección completa del cliente"
              />
            </div>

            {/* Estado */}
            <div className="md:col-span-2">
              <label className="flex items-center">
                <input
                  type="checkbox"
                  name="activo"
                  checked={formData.activo || false}
                  onChange={handleInputChange}
                  className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                />
                <span className="ml-2 text-sm text-gray-700">Cliente activo</span>
              </label>
            </div>
          </div>
        )}

        {activeTab === 'exoneracion' && (
          <div>
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-lg font-medium text-gray-900">Exoneraciones</h3>
              <button
                type="button"
                onClick={addExoneracion}
                className="bg-green-600 hover:bg-green-700 text-white font-bold py-2 px-4 rounded-lg transition duration-200"
              >
                Agregar Exoneración
              </button>
            </div>

            {exoneraciones.length === 0 ? (
              <div className="text-center py-8 text-gray-500">
                No hay exoneraciones registradas
              </div>
            ) : (
              <div className="space-y-4">
                {exoneraciones.map((exoneracion, index) => (
                  <div key={index} className="border border-gray-200 rounded-lg p-4">
                    <div className="flex justify-between items-center mb-4">
                      <h4 className="font-medium text-gray-900">Exoneración {index + 1}</h4>
                      <button
                        type="button"
                        onClick={() => removeExoneracion(index)}
                        className="text-red-600 hover:text-red-800"
                      >
                        Eliminar
                      </button>
                    </div>
                    
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          Tipo de Documento *
                        </label>
                        <select
                          value={exoneracion.tipo_documento}
                          onChange={(e) => updateExoneracion(index, 'tipo_documento', e.target.value)}
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                        >
                          <option value="">Seleccionar tipo</option>
                          {Object.entries(TIPOS_DOCUMENTO).map(([key, value]) => (
                            <option key={key} value={key}>
                              {key} - {value}
                            </option>
                          ))}
                        </select>
                      </div>

                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          Número de Documento *
                        </label>
                        <input
                          type="text"
                          value={exoneracion.numero_documento}
                          onChange={(e) => updateExoneracion(index, 'numero_documento', e.target.value)}
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                          placeholder="Ej: LE-1234-2023"
                        />
                      </div>

                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          Institución *
                        </label>
                        <select
                          value={exoneracion.nombre_institucion}
                          onChange={(e) => updateExoneracion(index, 'nombre_institucion', e.target.value)}
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                        >
                          <option value="">Seleccionar institución</option>
                          {Object.entries(INSTITUCIONES).map(([key, value]) => (
                            <option key={key} value={key}>
                              {key} - {value}
                            </option>
                          ))}
                        </select>
                      </div>

                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          Fecha de Emisión *
                        </label>
                        <input
                          type="date"
                          value={exoneracion.fecha_emision}
                          onChange={(e) => updateExoneracion(index, 'fecha_emision', e.target.value)}
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                        />
                      </div>

                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          Tarifa *
                        </label>
                        <input
                          type="text"
                          value={exoneracion.tarifa}
                          onChange={(e) => updateExoneracion(index, 'tarifa', e.target.value)}
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                          placeholder="Ej: 13"
                        />
                      </div>

                      {/* Campos condicionales de Artículo e Inciso */}
                      {requiresArticuloInciso(exoneracion.tipo_documento) && (
                        <>
                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">
                              Artículo *
                            </label>
                            <input
                              type="text"
                              value={exoneracion.articulo || ''}
                              onChange={(e) => updateExoneracion(index, 'articulo', e.target.value)}
                              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                              placeholder="Ej: 12"
                            />
                          </div>

                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">
                              Inciso *
                            </label>
                            <input
                              type="text"
                              value={exoneracion.inciso || ''}
                              onChange={(e) => updateExoneracion(index, 'inciso', e.target.value)}
                              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                              placeholder="Ej: 5"
                            />
                          </div>
                        </>
                      )}

                      {/* Información sobre campos requeridos */}
                      {exoneracion.tipo_documento && (
                        <div className="md:col-span-2">
                          <div className={`p-3 rounded-lg text-sm ${
                            requiresArticuloInciso(exoneracion.tipo_documento)
                              ? 'bg-blue-50 text-blue-700 border border-blue-200'
                              : 'bg-gray-50 text-gray-600 border border-gray-200'
                          }`}>
                            <div><strong>Tipo de documento:</strong> {getTipoDocumentoLabel(exoneracion.tipo_documento)}</div>
                            {exoneracion.nombre_institucion && (
                              <div><strong>Institución:</strong> {getInstitucionLabel(exoneracion.nombre_institucion)}</div>
                            )}
                            {requiresArticuloInciso(exoneracion.tipo_documento) && (
                              <div className="mt-1">
                                <strong>Nota:</strong> Este tipo requiere los campos Artículo e Inciso.
                              </div>
                            )}
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {errors.submit && (
          <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded mb-4">
            {errors.submit}
          </div>
        )}

        {/* Botones de acción */}
        <div className="flex justify-end space-x-4 mt-6 pt-6 border-t border-gray-200">
          <button
            type="button"
            onClick={onCancel}
            className="px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 transition duration-200"
          >
            Cancelar
          </button>
          <button
            type="submit"
            disabled={loading}
            className="px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white font-bold rounded-lg transition duration-200 disabled:opacity-50"
          >
            {loading ? 'Guardando...' : 'Guardar Cliente'}
          </button>
        </div>
      </form>
    </div>
  );
};

export default ClienteForm; 